// forms/sow.schema.ts
export type FieldType =
  | "select"
  | "text"
  | "textarea"
  | "date"
  | "currency"
  | "radio";

export type Condition =
  | { field: string; equals: any }
  | { field: string; notEquals: any }
  | { field: string; in: any[] }
  | { field: string; truthy: true };

export interface FormField {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  helpText?: string;
  placeholder?: string;
  options?: { label: string; value: string }[];
  showWhen?: Condition[];          // all conditions must be met
  requireWhen?: Condition[];       // if true => required
}

export interface FormSchema {
  requestType: "SOW";
  title: string;
  description?: string;
  fields: FormField[];
}

export const SOW_SCHEMA: FormSchema = {
  requestType: "SOW",
  title: "HDM Client Contract Request Form",
  description:
    `Automation updates pending. Notify Ken if any issues or suggestions`,
  fields: [
    {
      name: "documentType",
      label: "Document Type",
      type: "select",
      required: true,
      options: [
        { label: "SOW", value: "SOW" },
        { label: "NDA", value: "NDA" },
        { label: "Other", value: "Other" },
      ],
    },

    {
      name: "clientName",
      label: "Client Name",
      type: "text",
      required: true,
      helpText:
        "What you enter here is what will appear in the contract preamble and signature line — please use the full legal name.",
    },
    { name: "accountManagerRequester", label: "Account Manager/Requester", type: "text", required: true },

    { name: "sowStartDate", label: "SOW Start Date", type: "date", required: true, helpText: "Date format: mm/dd/yyyy" },
    { name: "newSowEndDate", label: "New SOW End Date", type: "date", required: true, helpText: "Date format: mm/dd/yyyy" },

    {
      name: "msaSignatureDate",
      label: "Corresponding MSA signature date",
      type: "text",
      required: true,
      helpText: "Date format: mmmm d, yyyy",
      placeholder: "January 5, 2026",
    },

    {
      name: "hasCustomSowTemplate",
      label: "Does this client have a custom SOW template?",
      type: "radio",
      required: true,
      options: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
      ],
      helpText: 'If yes, provide a link to the template in Comments.',
    },

    {
      name: "sowType",
      label: "SOW Type",
      type: "select",
      required: true,
      helpText:
        "Basic T&E (time & expenses) is what we use for most staff aug work. Fixed scope options will generate a deliverables-based SOW template.",
      options: [
        { label: "Basic T&E", value: "Basic T&E" },
        { label: "Fixed Scope", value: "Fixed Scope" },
        { label: "SOW Special Handling", value: "SOW Special Handling" },
      ],
    },

    { name: "opportunity", label: "Opportunity", type: "text", required: false },

    { name: "hdmPointOfContact", label: "HDM Point of Contact", type: "text", required: true },

    {
      name: "projectName",
      label: "Name of Project",
      type: "text",
      required: true,
      helpText: "This will become the title of the SOW and appear in the document introduction.",
    },
    {
      name: "projectDescription",
      label: "Description of Project",
      type: "textarea",
      required: true,
      helpText: 'This will be listed as "Scope of Work" on the SOW, right under "Name of Project".',
    },

    {
      name: "workLocation",
      label: "Will the majority of work be conducted remotely or onsite?",
      type: "select",
      required: true,
      options: [
        { label: "Remote", value: "Remote" },
        { label: "Onsite", value: "Onsite" },
        { label: "Hybrid", value: "Hybrid" },
      ],
    },

    {
      name: "travelIncludedInRate",
      label: "Travel expenses are/are not included in the hourly rate?",
      type: "select",
      required: true,
      helpText:
        "Default is that travel expenses ARE NOT included in the hourly rate (applies to fully remote projects too). Select ARE if this is an exception.",
      options: [
        { label: "ARE included", value: "ARE" },
        { label: "ARE NOT included", value: "ARE_NOT" },
      ],
    },

    {
      name: "travelBudget",
      label: "If there is a travel budget for this SOW, what is it?",
      type: "currency",
      required: false,
      helpText: 'If the project is fully remote, write 0. Format: $',
    },

    {
      name: "travelBudgetType",
      label: "If there is a travel budget for this SOW, is it an estimate or a firm limit?",
      type: "select",
      required: false,
      helpText: `For estimate, select "are estimated to be," and for a firm limit, select "should not exceed"`,
      options: [
        { label: "are estimated to be", value: "estimate" },
        { label: "should not exceed", value: "limit" },
      ],
      showWhen: [{ field: "travelBudget", truthy: true }],
    },

    {
      name: "hasTotalAllocationAmount",
      label: "Will there be a total allocation amount for this SOW?",
      type: "radio",
      required: true,
      helpText:
        `Only a couple of clients require this. If the client has not specified a contractual "shall not exceed" amount, answer "No."`,
      options: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
      ],
    },

    { name: "dateMustSendBy", label: "Date SOW must be sent to client by", type: "date", required: false, helpText: "Date format: mm/dd/yyyy" },

    {
      name: "servicesToBePerformed",
      label: "Services to be Performed",
      type: "textarea",
      required: true,
      helpText:
        `"HDM will work with [Customer] during the term of this SOW to…"
Usually bullet list/tasks/activities or a couple sentences describing what resource(s) will do.`,
    },

    { name: "clientSigningAuthorityName", label: "SOW Client Signing Authority Name", type: "text", required: false },
    { name: "clientSigningAuthorityTitle", label: "SOW Client Signing Authority Title", type: "text", required: false },

    {
      name: "hdmSigner",
      label: "HDM Signer (if known)",
      type: "select",
      required: false,
      helpText: "Which member of senior leadership will be executing this contract? (If not listed, use comments.)",
      options: [
        { label: "— Select —", value: "" },
        { label: "Leadership A", value: "Leadership A" },
        { label: "Leadership B", value: "Leadership B" },
      ],
    },

    {
      name: "comments",
      label: "Comments",
      type: "textarea",
      required: false,
      helpText:
        `Add here:
- Anything else we should know about the contract or project
- Links to custom templates
- Contract delivery instructions: email or DocuSign? If DocuSign, include client signer name/title/email + cover message subject & contents`,
      requireWhen: [{ field: "hasCustomSowTemplate", equals: "Yes" }],
    },
  ],
};
